import { QueryClientProvider } from "@tanstack/react-query";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { Store } from "../shared/types";
import { GOOGLE_MAPS_API_KEY } from "./lib/env";
import { queryClient } from "./lib/query";
import { cn } from "./lib/classNames";
import { ROUTES, useCurrentPath } from "./lib/router";
import { distanceKm } from "./lib/geoUtils";
import { PREF_CENTROIDS, UNKNOWN_PREF_DISTANCE_KM } from "./lib/prefCentroids";
import { useBreakpoint } from "./hooks/useBreakpoint";
import { useGeolocation } from "./hooks/useGeolocation";
import { useResolvedStores } from "./hooks/useResolvedStores";
import { useStores, type StoresFilter } from "./hooks/useStores";
import { Header } from "./components/Header";
import { FilterBar } from "./components/FilterBar";
import { HamburgerMenu } from "./components/HamburgerMenu";
import { LocateButton } from "./components/LocateButton";
import { StoreList } from "./components/StoreList";
import { MapView } from "./components/MapView";
import { StoreSheet } from "./components/StoreSheet";
import { AboutPage } from "./pages/AboutPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { DisclaimerPage } from "./pages/DisclaimerPage";

// Progressive resolve 設定 ——
// INITIAL_BATCH 件以下: 一括で resolve (従来の即時体験を維持)。
// それを超える分: BATCH_SIZE ずつ BATCH_INTERVAL_MS 間隔で順次 resolve に回す。
// ユーザーが途中でフィルタ/検索を変更すると未発火分は捨てられるため、
// 初回の bulk resolve で「全件分まとめて課金」を回避できる。
const INITIAL_BATCH = 150;
const BATCH_SIZE = 30;
const BATCH_INTERVAL_MS = 400;

// マップに表示するピンの最大件数。ユーザーが Header の dropdown で変更可能。
const DEFAULT_MAX_PIN_COUNT = 200;
const MIN_MAX_PIN_COUNT = 100;
const MAX_MAX_PIN_COUNT = 1000;

// 初期表示のデフォルトフィルタ。エリアは TOKYO、年度・選出回数は未指定 (= 全年度・指定なし)。
// 全件取得すると Places resolve コストが大きいので area スコープでガードしている。
// 件数が多くなる場合は progressive resolve (INITIAL_BATCH + setInterval) に乗せて
// バーストを抑える設計。閉店・移転は showInactive の初期値 false で除外される。
const DEFAULT_FILTER: StoresFilter = { area: "tokyo" };

// storesQuery.data が undefined の間も参照を安定させるための sentinel。
// `?? []` で毎 render 新しい空配列を作ると下流の useEffect が誤って
// 再発火してしまうため、モジュール定数で固定する。
const EMPTY_STORES: Store[] = [];

/** NFKC 正規化 + lowercase + 空白除去。半角/全角カタカナ・大小文字・空白ゆれを吸収。 */
function normalizeForSearch(s: string): string {
  return s.normalize("NFKC").toLowerCase().replace(/\s+/g, "");
}

function Shell() {
  const isDesktop = useBreakpoint("(min-width: 1024px)");
  const [filter, setFilter] = useState<StoresFilter>(DEFAULT_FILTER);
  const [nameQuery, setNameQuery] = useState("");
  // 日本語 IME 変換中は nameQuery を変更しても部分文字列が確定前に流れ、
  // progressive batch が無駄に再起動する。compositionend まで適用を遅らせる。
  const [committedQuery, setCommittedQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [maxPinCount, setMaxPinCount] = useState<number>(DEFAULT_MAX_PIN_COUNT);
  // 閉店 / 移転店舗を表示するかどうか。デフォルトは除外 (営業中のみ)。
  const [showInactive, setShowInactive] = useState<boolean>(false);
  // SP 専用 UI state ——
  // - filterExpanded: SP の compact filter で年度/選出回数 Row を展開するか
  // - showMobileList: SP で StoreList を全画面オーバーレイ表示するか
  // - searchVisible: SP で SearchInput を表示するか (縦スペース節約のためデフォルト非表示)
  const [filterExpanded, setFilterExpanded] = useState<boolean>(false);
  const [showMobileList, setShowMobileList] = useState<boolean>(false);
  const [searchVisible, setSearchVisible] = useState<boolean>(false);
  // マップ上のピン表示モード。デフォルトは集約 (SP 負荷が軽い)。
  // false にすると全マーカーを個別描画する (見た目優先だが多ピン時は重い)。
  const [clusterMode, setClusterMode] = useState<boolean>(true);
  // 現在地ベースの「周辺検索」機能 state。off / loading / active / error のステートマシン。
  // active 時は area/year/minSelections フィルタをバイパスし (検索と同じ挙動)、
  // 全店舗から現在地に近い順に progressive resolve する。
  const { state: nearbyState, requestLocation, clear: clearNearby } =
    useGeolocation();
  const nearbyActive = nearbyState.status === "active";
  const userLocation = nearbyActive ? nearbyState.location : null;
  // PC/SP 共通 ハンバーガーメニュー
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  // ワード検索が確定済みのときはサーバ側の area/year 絞り込みを無効化して
  // 全件取得する。そうしないと現在の絞り込み外の店舗 (例: tokyo 絞り込み中の神奈川店)
  // が検索結果に含まれない。React Query は queryKey で別キャッシュを持つので、
  // 検索をクリアすれば元のフィルタ結果に瞬時に戻る。
  //
  // nearby mode は現在の絞り込みを尊重する (= bypass しない)。クライアント側で
  // 距離ソートだけを掛けるので、ユーザーが選んだエリア/年度の中から「近い順」に
  // 並ぶ挙動になる。以前は nearby も絞り込みを無効化していたが、そのため
  // FilterBar が「ワード検索中は無効」状態に入ってしまい UI 的に誤認を招いていた。
  const wordSearchActive = committedQuery.length > 0;
  const effectiveFilter: StoresFilter = wordSearchActive ? {} : filter;
  const storesQuery = useStores(effectiveFilter);
  const allStores: Store[] = storesQuery.data?.stores ?? EMPTY_STORES;

  // クライアントサイド絞り込み: 閉店/移転トグル + 店名検索 (部分一致 + 正規化)。
  // /api/stores へ送らないので追加リクエストは発生しない。
  //
  // 特例: ワード検索が active のときは showInactive を強制的に true 扱いにする。
  // 理由: ユーザーが特定の店名を検索しているときは「その店舗が閉店/移転してい
  // ても結果に含めたい」のが自然 (例: 閉店した店を懐かしみたいケース)。
  // 検索をクリアすれば元の showInactive 設定に戻る。
  const stores = useMemo(() => {
    let result = allStores;
    const q = normalizeForSearch(committedQuery);
    const searchActive = q.length > 0;
    if (!showInactive && !searchActive) {
      // status === "closed" / movedToClosed の連鎖閉店、moved 全般、renewed を除外
      result = result.filter(
        (s) =>
          s.status !== "closed" &&
          s.status !== "moved" &&
          s.status !== "renewed" &&
          s.movedToClosed !== true,
      );
    }
    if (q) {
      result = result.filter((s) => normalizeForSearch(s.name).includes(q));
    }
    // nearby mode: 現在地に近い順に並び替える (pref centroid ベースの粗いソート)。
    // progressive resolve が先頭から N 件だけ resolve する仕様を活用し、
    // 「近い店舗から先に Google 情報を取ってくる」動線を作る。
    // lat/lng は resolve 後しか得られないので pref 文字列で近似する。
    if (userLocation) {
      const withDistance = result.map((s) => {
        const centroid = s.pref ? PREF_CENTROIDS[s.pref] : undefined;
        const d = centroid
          ? distanceKm(userLocation, centroid)
          : UNKNOWN_PREF_DISTANCE_KM;
        return { store: s, d };
      });
      withDistance.sort((a, b) => a.d - b.d);
      result = withDistance.map((x) => x.store);
    }
    return result;
  }, [allStores, committedQuery, showInactive, userLocation]);

  // 表示対象の上限件数。ユーザー指定の maxPinCount と filter 結果数の小さい方。
  const targetCount = Math.min(maxPinCount, stores.length);

  // Progressive resolve: stores / targetCount が変わるたびに先頭 INITIAL_BATCH 件
  // (ただし targetCount を超えない) を即座に resolve 対象とし、残りは setInterval で
  // targetCount まで順次追加していく。cleanup で interval を cancel するため、
  // フィルタ変更や上限変更時の未発火分は捨てられる。
  //
  // stores 参照を ref で追跡し、stores が変わらず maxPinCount だけ変わったケース
  // (= 上限を上下させただけ) では resolveLimit を「現状値より下げない」ようにして
  // 上限を上げたときに一旦 150 に縮んでから伸び直すフラッシュを防ぐ。
  const [resolveLimit, setResolveLimit] = useState(INITIAL_BATCH);
  const prevStoresRef = useRef(stores);
  useEffect(() => {
    const storesChanged = prevStoresRef.current !== stores;
    prevStoresRef.current = stores;

    setResolveLimit((prev) => {
      const initial = Math.min(INITIAL_BATCH, targetCount);
      if (storesChanged) {
        // フィルタ/検索変更: 完全リセット
        return initial;
      }
      // maxPinCount のみの変更: 上限以下にクランプしつつ既存進捗は保持
      return Math.max(initial, Math.min(prev, targetCount));
    });

    if (targetCount <= INITIAL_BATCH) return;
    const timer = window.setInterval(() => {
      setResolveLimit((prev) => {
        const next = prev + BATCH_SIZE;
        if (next >= targetCount) {
          window.clearInterval(timer);
          return targetCount;
        }
        return next;
      });
    }, BATCH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [stores, targetCount]);

  const idsToResolve = useMemo(
    () => stores.slice(0, resolveLimit).map((s) => s.id),
    [stores, resolveLimit],
  );
  const resolved = useResolvedStores(idsToResolve);

  const isBatching = targetCount > INITIAL_BATCH && resolveLimit < targetCount;
  const isCapped = stores.length > maxPinCount;

  // 移転先 store への遷移。target が現在のフィルタにすでに含まれていれば
  // フィルタは触らない (不要な再 fetch + progressive batch リセットを避ける)。
  // 含まれていない場合のみ area スコープのフィルタに切り替えて target を可視化する。
  const handleNavigate = useCallback(
    (storeId: string) => {
      const alreadyVisible = stores.some((s) => s.id === storeId);
      if (!alreadyVisible) {
        // movedToArea が StoreSeed に埋め込まれているので allStores の外でも area を解決できる
        const fromMoved = stores.find((s) => s.movedToId === storeId);
        const area = fromMoved?.movedToArea;
        if (area) {
          setFilter({ area });
          setNameQuery("");
          setCommittedQuery("");
        }
      }
      setSelectedId(storeId);
    },
    [stores],
  );

  // SP の StoreList オーバーレイから選択したときのハンドラ。
  // useCallback で安定化し、memo 化した StoreListRow の再レンダリングを防ぐ。
  const handleMobileListSelect = useCallback((id: string) => {
    setSelectedId(id);
    // 選択したらリストを閉じてマップに戻し、StoreSheet を開く。
    // MapView の FitBounds が selectedId を見て panTo + zoom する。
    setShowMobileList(false);
  }, []);

  // Header を React.memo 化したので、onMenuOpen も安定参照が必須。
  const handleMenuOpen = useCallback(() => setMenuOpen(true), []);
  const handleMenuClose = useCallback(() => setMenuOpen(false), []);
  const handleSheetClose = useCallback(() => setSelectedId(null), []);

  // 選択店舗の lookup は毎 render で呼ばれる hot path。stores.find は O(n) で
  // 200 件規模に対して毎 render 実行されるのを防ぐため useMemo で memo する。
  const selectedStore = useMemo(
    () =>
      selectedId !== null
        ? stores.find((s) => s.id === selectedId) ?? null
        : null,
    [stores, selectedId],
  );
  const selectedResolved = useMemo(
    () => (selectedId !== null ? resolved.byId.get(selectedId) ?? null : null),
    [resolved.byId, selectedId],
  );

  return (
    <div className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-slate-950 text-slate-100">
      <Header
        displayCount={targetCount}
        filteredCount={stores.length}
        resolvedCount={resolved.byId.size}
        isLoading={storesQuery.isLoading || resolved.isLoading}
        maxPinCount={maxPinCount}
        onMaxPinCountChange={setMaxPinCount}
        minPinCount={MIN_MAX_PIN_COUNT}
        maxPinCountLimit={MAX_MAX_PIN_COUNT}
        onMenuOpen={handleMenuOpen}
      />
      <div className="relative flex min-h-0 flex-1">
        {isDesktop && (
          <aside className="z-20 flex w-[360px] shrink-0 flex-col border-r border-white/10 bg-slate-950/90 backdrop-blur">
            <FilterBar
              filter={filter}
              onChange={setFilter}
              nameQuery={nameQuery}
              onNameQueryChange={setNameQuery}
              onNameQueryCommit={setCommittedQuery}
              // 「ワード検索中は絞り込み無効」dim + warning はワード検索のみで発火。
              // nearby mode はエリア/年度フィルタを尊重するので dim しない。
              searchActive={wordSearchActive}
              showInactive={showInactive}
              onShowInactiveChange={setShowInactive}
              clusterMode={clusterMode}
              onClusterModeChange={setClusterMode}
            />
            <StoreList
              stores={stores}
              isLoading={storesQuery.isLoading}
              onSelect={setSelectedId}
              selectedId={selectedId}
            />
          </aside>
        )}
        <main className="relative min-h-0 flex-1">
          <MapView
            stores={stores}
            resolvedById={resolved.byId}
            selectedId={selectedId}
            onSelect={setSelectedId}
            searchActive={committedQuery.length > 0}
            clusterMode={clusterMode}
            userLocation={userLocation}
          />
          {isBatching && (
            <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center px-4">
              <div className="pointer-events-auto rounded-full border border-white/15 bg-slate-900/85 px-4 py-2 text-xs text-slate-200 shadow-xl shadow-black/40 backdrop-blur">
                {resolveLimit.toLocaleString()} / {targetCount.toLocaleString()} 件を順次読み込み中...
                {isCapped && (
                  <span className="ml-1 text-slate-400">
                    (全{stores.length.toLocaleString()}件)
                  </span>
                )}
              </div>
            </div>
          )}
          {!isDesktop && (
            <>
              <div className="pointer-events-none absolute inset-x-0 top-0 z-10 p-2.5">
                <div className="pointer-events-auto">
                  <FilterBar
                    filter={filter}
                    onChange={setFilter}
                    nameQuery={nameQuery}
                    onNameQueryChange={setNameQuery}
                    onNameQueryCommit={setCommittedQuery}
                    searchActive={wordSearchActive}
                    showInactive={showInactive}
                    onShowInactiveChange={setShowInactive}
                    clusterMode={clusterMode}
                    onClusterModeChange={setClusterMode}
                    compact
                    expanded={filterExpanded}
                    onExpandToggle={() => setFilterExpanded((v) => !v)}
                    // searchVisible がこの toggle の単一責任なので、X クリアを
                    // 踏んでも消えない (SearchInput の ✕ は query だけ消す)。
                    // toggle を「閉じる」操作はクエリクリアも兼ねる (下の onClick で実施)。
                    searchVisible={searchVisible}
                    maxPinCount={maxPinCount}
                    onMaxPinCountChange={setMaxPinCount}
                    minPinCount={MIN_MAX_PIN_COUNT}
                    maxPinCountLimit={MAX_MAX_PIN_COUNT}
                    trailingActions={
                      <button
                        type="button"
                        onClick={() => {
                          if (searchVisible) {
                            // 閉じる時は入力値も一緒にクリアする。
                            // そうしないと「hidden なのに検索がかかったまま」という
                            // 乖離状態が残り、ユーザーが全件表示に戻したつもりで
                            // 実はクエリが効き続けるバグを引き起こす。
                            setNameQuery("");
                            setCommittedQuery("");
                            setSearchVisible(false);
                          } else {
                            setSearchVisible(true);
                          }
                        }}
                        aria-pressed={searchVisible}
                        aria-label={searchVisible ? "検索を閉じる" : "店名で検索"}
                        className={cn(
                          "flex h-8 w-8 items-center justify-center rounded-full border transition",
                          searchVisible
                            ? "border-sky-400/50 bg-sky-500/20 text-sky-100"
                            : "border-white/15 bg-slate-800/80 text-slate-200 hover:bg-slate-700",
                        )}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <circle cx="11" cy="11" r="7" />
                          <path d="m20 20-3.5-3.5" />
                        </svg>
                      </button>
                    }
                  />
                </div>
              </div>
              {/* 左下スタック: 上段に現在地ボタン、下段に店舗一覧 pill。
                  (1) SP ヘッダーから件数を外して縦スペースを確保、
                  (2) 「一覧」というテキスト + 🗒 アイコン + 件数で「これが店舗一覧
                      ボタンだ」と一目でわかるアフォーダンスを提供する。
                  現在地ボタンは PC では右上に置くが、SP では右上が FilterBar の
                  展開領域とぶつかりやすいので、左下スタックの一番上に積む。
                  座標は safe-area 考慮のため env(safe-area-inset-bottom) を含む。
                  bottom-2 で下端からわずかに浮かせ、画面下端とのクリアランスを確保する。 */}
              <div className="pointer-events-none absolute bottom-2 left-0 z-10 flex flex-col items-start gap-2.5 p-3 safe-bottom">
                <LocateButton
                  state={nearbyState}
                  onRequest={requestLocation}
                  onClear={clearNearby}
                />
                <button
                  type="button"
                  onClick={() => setShowMobileList(true)}
                  aria-label={`店舗一覧を開く (${stores.length}件)`}
                  className="pointer-events-auto flex items-center gap-2 rounded-full border border-white/15 bg-slate-900/95 py-2 pl-3 pr-4 text-xs font-semibold text-slate-100 shadow-lg shadow-black/50 transition hover:bg-slate-800/95 active:scale-95"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                    className="text-rose-300"
                  >
                    <line x1="8" y1="6" x2="21" y2="6" />
                    <line x1="8" y1="12" x2="21" y2="12" />
                    <line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" />
                    <line x1="3" y1="12" x2="3.01" y2="12" />
                    <line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                  <span>店舗一覧</span>
                  <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-slate-200">
                    {resolved.byId.size}/{targetCount}
                    {isCapped && (
                      <span className="ml-0.5 text-slate-400">
                        /{stores.length}
                      </span>
                    )}
                  </span>
                </button>
              </div>
            </>
          )}
          {/* PC のみ: 現在地ボタンを map 右上 (header 直下) に固定配置する。
              SP では代わりに上の「左下スタック」に畳み込んでいる。
              再タップで nearby mode を解除し、元のフィルタ結果を復帰する。 */}
          {isDesktop && (
            <div className="pointer-events-none absolute right-0 top-0 z-10 p-3">
              <LocateButton
                state={nearbyState}
                onRequest={requestLocation}
                onClear={clearNearby}
              />
            </div>
          )}
        </main>
      </div>
      {!isDesktop && showMobileList && (
        <div className="absolute inset-0 z-40 flex flex-col bg-slate-950">
          <div className="safe-top flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 pb-2.5">
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold text-slate-50">
                店舗一覧
              </h2>
              <p className="text-[11px] text-slate-400">
                {stores.length.toLocaleString()} 件
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowMobileList(false)}
              aria-label="店舗一覧を閉じる"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-slate-800/80 text-slate-200 transition hover:bg-slate-700"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <StoreList
            stores={stores}
            isLoading={storesQuery.isLoading}
            selectedId={selectedId}
            onSelect={handleMobileListSelect}
          />
        </div>
      )}
      {/* 常時マウントしていた StoreSheet を conditional に変更。
          閉じている間も backdrop-blur-xl + shadow-2xl の合成コストを払っていた
          (特に SP で顕著)。選択がないときは完全にアンマウントして compositor から外す。
          スライドイン transition は失うが、開閉の応答は即時になる。 */}
      {selectedStore && (
        <StoreSheet
          store={selectedStore}
          resolved={selectedResolved}
          onClose={handleSheetClose}
          onNavigate={handleNavigate}
          variant={isDesktop ? "side" : "bottom"}
        />
      )}
      <HamburgerMenu open={menuOpen} onClose={handleMenuClose} />
    </div>
  );
}

/**
 * ルート分岐: /about, /privacy, /terms, /disclaimer は静的ページを、
 * それ以外はメインの Map SPA (`<Shell>`) を描画する。
 *
 * Cloudflare Workers の `not_found_handling: "single-page-application"`
 * により、未知パスはすべて index.html にフォールバックする → この関数で
 * pathname を見て表示コンポーネントを切り替える。
 */
function RouteRoot() {
  const path = useCurrentPath();
  switch (path) {
    case ROUTES.about:
      return <AboutPage />;
    case ROUTES.privacy:
      return <PrivacyPage />;
    case ROUTES.terms:
      return <TermsPage />;
    case ROUTES.disclaimer:
      return <DisclaimerPage />;
    default:
      return <Shell />;
  }
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <APIProvider
        apiKey={GOOGLE_MAPS_API_KEY}
        language="ja"
        region="JP"
        libraries={["marker"]}
      >
        <RouteRoot />
      </APIProvider>
    </QueryClientProvider>
  );
}
